// Toast primitive + ToastProvider/useToast hook.
// 패턴 참고: apps/api/public/app-mini/index.html:259-280 (.toast .show CSS) +
//           apps/api/public/app-mini/index.html:596-611 (showToast 호출 패턴).
// 우하단 fixed area, slide-in motion (tokens.css 의 --ease-out, --dur-base), 최대 3개 stack.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { Icon } from './icons';

export type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  duration: number;
}

interface ToastApi {
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const MAX_STACK = 3;
const DEFAULT_MS = 3200;

const toneVisual: Record<
  ToastTone,
  { bg: string; fg: string; icon: string; iconColor: string }
> = {
  success: {
    bg: 'var(--success-50)',
    fg: '#14532D',
    icon: 'check',
    iconColor: 'var(--success)',
  },
  error: {
    bg: 'var(--danger-bg)',
    fg: '#7F1D1D',
    icon: 'alert',
    iconColor: 'var(--danger)',
  },
  info: {
    bg: 'var(--info-bg)',
    fg: '#075985',
    icon: 'alert',
    iconColor: 'var(--info)',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const enqueue = useCallback((tone: ToastTone, message: string, duration?: number) => {
    const id = Date.now() + Math.random();
    const ms = duration ?? DEFAULT_MS;
    setToasts((prev) => {
      const next = [...prev, { id, tone, message, duration: ms }];
      // Trim oldest if exceeding stack
      return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
    });
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  const api: ToastApi = {
    success: (m, d) => enqueue('success', m, d),
    error: (m, d) => enqueue('error', m, d),
    info: (m, d) => enqueue('info', m, d),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'fixed',
          right: 'var(--space-4)',
          bottom: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          zIndex: 1100,
          pointerEvents: 'none',
          maxWidth: 'min(90vw, 380px)',
        }}
      >
        {toasts.map((t) => (
          <ToastView key={t.id} item={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
      <style>{`
        @keyframes dvvb-toast-in {
          from { opacity: 0; transform: translateX(16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const v = toneVisual[item.tone];
  // Pause auto-dismiss not implemented — just visual.
  useEffect(() => {
    return () => {
      // cleanup placeholder
    };
  }, []);
  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-2)',
        padding: '10px 14px',
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.iconColor}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        fontSize: 13,
        fontFamily: 'var(--font-sans)',
        lineHeight: 'var(--lh-body)',
        animation: 'dvvb-toast-in var(--dur-base) var(--ease-out)',
      }}
    >
      <Icon name={v.icon} size={16} color={v.iconColor} />
      <span style={{ flex: 1 }}>{item.message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        style={{
          background: 'transparent',
          border: 'none',
          color: v.fg,
          opacity: 0.6,
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
        }}
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
